/**
 * @fileoverview Per-section React error boundary (G-005).
 * @module inspection-renderer/sections
 *
 * Responsibilities:
 * - Catch render-time errors inside ONE section so an unrelated section can
 *   continue to render. Surfaces a small `role="alert"` panel with the field
 *   id chain when available.
 * - G-005 — the legacy mobile-pwa renderer crashed the whole form when a
 *   single widget threw; this boundary ends that class of bug.
 *
 * Dependencies:
 * - React 18 class-component error boundary API (no hook-equivalent yet).
 *
 * Architecture:
 * - Presentation-only. Errors are logged via the injected `onError` callback
 *   so the host can wire its existing telemetry / Sentry pipeline.
 *
 * @errorHandling
 * Strategy: catch, render panel, surface to host. Never rethrow.
 * User Impact: section panel replaces the broken widget; rest of form OK.
 * Logging: REQUIRED via `onError` (host wires to Sentry / app logger).
 *
 * @contract SectionErrorBoundaryProps
 * Purpose: prop contract for the per-section render-time error catcher.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface SectionErrorBoundaryProps {
  sectionId: string;
  sectionTitle?: string;
  onError?: (err: Error, info: ErrorInfo, sectionId: string) => void;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info, this.props.sectionId);
  }

  render() {
    const { error } = this.state;
    const { sectionId, sectionTitle, children } = this.props;
    if (!error) return children;
    return (
      <div
        role="alert"
        data-section-id={sectionId}
        data-section-error="true"
        className="border border-red-200 bg-red-50 rounded-sm p-3 my-2"
      >
        <p className="text-sm font-semibold text-red-700">
          Section &ldquo;{sectionTitle ?? sectionId}&rdquo; failed to render
        </p>
        <p className="text-xs text-red-600 mt-1">
          {error.message}
        </p>
      </div>
    );
  }
}
